import type { Container } from "pixi.js";
import type { PresentationTaskRunner } from "@slot/presentation";
import type { ModalChrome } from "@slot/game-shell";

/**
 * The feature panels' motion values (seconds of the chrome runner; scales are multipliers): the Cocos
 * `FeaturePanelAnimTuning`, now this title's own view tuning (decision 40 #21).
 */
export interface PanelMotionTuning {
  /** The panel's enter: from this scale up to 1 (backOut) while it fades in. */
  enterScaleFrom: number;
  enterDurationSeconds: number;
  /** The panel's exit: down to this scale (quadIn) while it fades out. */
  exitScaleTo: number;
  exitDurationSeconds: number;
  /** The "tap anywhere" hint's fades. */
  continueFadeInSeconds: number;
  continueFadeOutSeconds: number;
  /** The idle scale-pulse on the panel while it shows: its peak and one half-cycle. A peak of 1 is no pulse. */
  loopPanelScaleMax: number;
  loopPanelHalfSeconds: number;
  /** The idle scale-pulse on the title. */
  loopTitleScaleMax: number;
  loopTitleHalfSeconds: number;
}

/**
 * A feature panel's motion: the Cocos `FeaturePanelAnimatorBase` choreography over the shell's `ModalChrome`. Seated
 * hidden before it shows (no frame shows the last exit's end); on enter the panel scales up from `enterScaleFrom` while
 * it fades in, with its cover background and, for a panel that has one, the dim; once in, the panel and the title
 * pulse. On exit the pulses stop and the panel scales to `exitScaleTo` and fades out with everything it brought.
 * Everything runs on the chrome runner the `ModalChrome` fades on; a newer motion stops the one before it.
 */
export class PanelMotion {
  private generation = 0;

  public constructor(
    private readonly input: {
      chrome: ModalChrome;
      /** What scales and fades: the panel's text (and art). */
      panel: Container;
      /** What pulses on its own while the panel shows; none, no title pulse. */
      title?: Container | null;
      /** A cover background faded with the panel (the trigger's Spine background, the finished panel's art). */
      background?: Container | null;
      /** Whether the chrome's dim fades with the panel; a panel whose cover hides the game keeps it off. */
      dim: boolean;
      runner: PresentationTaskRunner;
      tuning: PanelMotionTuning;
      /** The idle pulses' scope on the chrome runner (one per panel). */
      loopScope: string;
    },
  ) {}

  /** The entry state, before the panel shows. Idempotent. */
  public preSeat(): void {
    const { chrome, panel, title, background, tuning } = this.input;
    this.generation += 1;
    chrome.stopFade(chrome.dim);
    chrome.stopFade(chrome.continueTextRoot);
    chrome.dim.alpha = 0;
    chrome.continueTextRoot.alpha = 0;
    panel.alpha = 0;
    panel.scale.set(tuning.enterScaleFrom, tuning.enterScaleFrom);
    title?.scale.set(1, 1);
    if (background) {
      chrome.stopFade(background);
      background.alpha = 0;
    }
  }

  /** The panel scaling and fading in, with its background and dim; the idle pulses start when it is in. */
  public async enter(): Promise<void> {
    this.preSeat();
    const { chrome, panel, background, dim, runner, tuning } = this.input;
    const generation = this.generation;
    await Promise.all([
      dim ? chrome.fade(chrome.dim, 1, tuning.enterDurationSeconds) : Promise.resolve(),
      background ? chrome.fade(background, 1, tuning.enterDurationSeconds) : Promise.resolve(),
      runner.tweenProgress(
        tuning.enterDurationSeconds,
        (k) => {
          if (generation !== this.generation || panel.destroyed) return;
          panel.alpha = k;
        },
        undefined,
        { easing: "linear" },
      ),
      runner.tweenProgress(
        tuning.enterDurationSeconds,
        (k) => {
          if (generation !== this.generation || panel.destroyed) return;
          const scale = tuning.enterScaleFrom + (1 - tuning.enterScaleFrom) * k;
          panel.scale.set(scale, scale);
        },
        undefined,
        { easing: "backOut" },
      ),
    ]);
    if (generation === this.generation) this.startIdlePulses(generation);
  }

  /** The panel scaling and fading out with the hint, its background and dim; resolves when all are done. */
  public async exit(): Promise<void> {
    const { chrome, panel, background, dim, runner, tuning } = this.input;
    const generation = ++this.generation;
    const fromScale = panel.scale.x;
    const fromAlpha = panel.alpha;
    await Promise.all([
      dim ? chrome.fade(chrome.dim, 0, tuning.exitDurationSeconds) : Promise.resolve(),
      background ? chrome.fade(background, 0, tuning.exitDurationSeconds) : Promise.resolve(),
      chrome.fade(chrome.continueTextRoot, 0, tuning.continueFadeOutSeconds),
      runner.tweenProgress(
        tuning.exitDurationSeconds,
        (k) => {
          if (generation !== this.generation || panel.destroyed) return;
          const scale = fromScale + (tuning.exitScaleTo - fromScale) * k;
          panel.scale.set(scale, scale);
          panel.alpha = fromAlpha * (1 - k);
        },
        undefined,
        { easing: "quadIn" },
      ),
    ]);
  }

  /** The "tap anywhere" hint, from 0. */
  public async fadeInContinueText(): Promise<void> {
    const { chrome, tuning } = this.input;
    chrome.stopFade(chrome.continueTextRoot);
    chrome.continueTextRoot.alpha = 0;
    await chrome.fade(chrome.continueTextRoot, 1, tuning.continueFadeInSeconds);
  }

  /** On hide: every tween and pulse stops where it is. */
  public stop(): void {
    const { chrome, background } = this.input;
    this.generation += 1;
    chrome.stopFade(chrome.dim);
    chrome.stopFade(chrome.continueTextRoot);
    if (background) chrome.stopFade(background);
  }

  /** The panel's and the title's breathing (a sine between 1 and the peak), until the next motion. */
  private startIdlePulses(generation: number): void {
    const { panel, title, runner, tuning } = this.input;
    const pulses: Array<{ node: Container; peak: number; half: number }> = [{ node: panel, peak: tuning.loopPanelScaleMax, half: tuning.loopPanelHalfSeconds }];
    if (title) pulses.push({ node: title, peak: tuning.loopTitleScaleMax, half: tuning.loopTitleHalfSeconds });
    for (const { node, peak, half } of pulses) {
      if (peak === 1 || half <= 0) continue;
      void runner.tickInScope(
        this.input.loopScope,
        (elapsedSeconds) => {
          const scale = 1 + (peak - 1) * 0.5 * (1 - Math.cos((Math.PI * elapsedSeconds) / half));
          node.scale.set(scale, scale);
        },
        () => generation !== this.generation || node.destroyed,
      );
    }
  }
}
